import { useMutation, useQueryClient } from '@tanstack/react-query';
import { APIMutationOptions } from 'shared/api/APIMutation';
import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
} from 'shared/api/APIQuery';
import { requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { ONE_DAY } from 'shared/constants/NumberConstants';
import { useDispatcherProfile } from 'shared/modules/dispatcher/DispatcherAPI';
import { OnboardingStep, OnboardingStepName } from './OnboardingDTO';
import {
  persistOnboardingStep,
  readOnboardingStep,
} from './onboardingStepStorage';
import { mergeOnboardingStep } from './OnboardingUtils';
import { useEffect } from 'react';

export function useOnboardingDetails(
  options?: APIQueryOptions<OnboardingStep>,
): APIQueryResult<OnboardingStep> {
  const { data: dispatcher } = useDispatcherProfile();
  const guid = dispatcher?.guid || '';
  const onboardingSteps = readOnboardingStep(guid);

  const onboarding = useAPIQuery<OnboardingStep>(
    ['onboarding', guid],
    () => {
      return requestCarrierAPI(
        `GET /internal/web/dispatchers/${guid}/onboarding/`,
      );
    },
    {
      initialData: onboardingSteps,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      ...options,
      enabled: !!guid && !onboardingSteps?.steps && (options?.enabled ?? true),
    },
  );

  useEffect(() => {
    if (onboarding.data && guid) {
      persistOnboardingStep(guid, onboarding.data, ONE_DAY);
    }
  }, [guid, onboarding.data]);

  return onboarding;
}

export interface SaveOnboardingProps {
  stepName: OnboardingStepName;
  status: 'completed' | 'skipped' | 'started';
  data?: Record<string, unknown>;
}

export function useSaveOnboardingStep(
  options?: APIMutationOptions<SaveOnboardingProps>,
) {
  const { data: dispatcher } = useDispatcherProfile();
  const queryClient = useQueryClient();
  const guid = dispatcher?.guid || '';

  return useMutation({
    mutationKey: ['onboarding-save-step', guid],
    mutationFn: ({ stepName, status, data }: SaveOnboardingProps) => {
      return requestCarrierAPI(
        `POST /internal/web/dispatchers/${guid}/onboarding/`,
        {
          json: { step_name: stepName, status, data },
        },
      );
    },

    ...options,
    onSuccess: (response, variables, onMutateResult, context) => {
      // POST returns `{ data: null }`, so there is nothing to write from the
      // response. Merge the saved step into the query cache as well as local
      // storage: the checklist reads the query cache, and it is not refetched
      // because the query is disabled once steps are cached.
      const current =
        queryClient.getQueryData<OnboardingStep>(['onboarding', guid]) ??
        readOnboardingStep(guid);
      const merged = mergeOnboardingStep({
        current,
        stepName: variables.stepName,
        status: variables.status,
      });

      if (merged && guid) {
        queryClient.setQueryData(['onboarding', guid], merged);
        persistOnboardingStep(guid, merged, ONE_DAY);
      }

      void options?.onSuccess?.(response, variables, onMutateResult, context);
    },
  });
}
